import { memo } from 'react';
import { AlertBanner } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { User, Users } from 'lucide-react';

import { EDIT_SCOPE } from '../../constants/editScope';

/** i18n key of the member banner: singular / plural by the other members, or its own text for none (English has no zero form). */
const withRolesKey = (otherMembers) =>
  otherMembers === 0 ? 'editScope.member.withRolesNoOthers' : 'editScope.member.withRoles';

/**
 * Banner of an editor that says who the change affects. `EDIT_SCOPE.ROLE` ("Changes apply to all
 * members with this role", also when the role is reached from a member) and `EDIT_SCOPE.MEMBER`
 * ("Only for <name>"). The copy makes no promise about when the change takes effect.
 *
 * @param {object} props
 * @param {'role'|'member'} props.scope One of `EDIT_SCOPE`.
 * @param {string} [props.name] Member name (member scope).
 * @param {string[]} [props.roles] Roles the member's access comes from (member scope); without
 *   any, the permissions are direct.
 * @param {number} [props.otherMembers] Other members who have those roles (member scope).
 */
const ScopeBanner = ({ scope, name, roles = [], otherMembers = 0 }) => {
  const { t } = useTranslation('people-access');

  if (scope === EDIT_SCOPE.ROLE) {
    return (
      <AlertBanner tone="warning" icon={Users} title={t('editScope.role.title')}>
        {t('editScope.role.message')}
      </AlertBanner>
    );
  }

  return (
    <AlertBanner tone="info" icon={User} title={t('editScope.member.title', { name })}>
      {roles.length
        ? t(withRolesKey(otherMembers), { name, roles: roles.join(', '), count: otherMembers })
        : t('editScope.member.direct')}
    </AlertBanner>
  );
};

export default memo(ScopeBanner);
